import { App, Empty, Flex, Input, Result } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getApiError,
    getPointInfoCollection,
    getSchedulerDayPointSchedule,
    getSchedulerDayPointScheduleExtra,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Point } from "tm-shared/types";

import { useOfflineModeSelector, useRouteSelector } from "@/hooks/redux";
import { AppDatePicker } from "@/shared/forms/AppDatePicker";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { Meta } from "@/shared/seo";

import { PointCardItem, PointCardSkeleton } from "./PointCardItem";

const ViewByDaysOfMonth = () => {
    const { notification } = App.useApp();

    const { routeId = "" } = useParams();

    const offlineMode = useOfflineModeSelector();
    const currentRoute = useRouteSelector(routeId);

    const [points, setPoints] = useState<Point[]>([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const getData = async () => {
            setLoading(true);
            setError("");
            setSearchQuery("");

            try {
                const formatDate = selectedDate.format(backendDateFormat.date);

                const [
                    { result: pointIdsFromServer },
                    { result: pointIdsExtraFromServer },
                ] = await Promise.all([
                    getSchedulerDayPointSchedule({
                        date: formatDate,
                        routeId,
                    }),
                    getSchedulerDayPointScheduleExtra({
                        date: formatDate,
                        agentId: currentRoute?.agentInfo?.id ?? "",
                    }),
                ]);

                const pointIds = [
                    ...pointIdsFromServer.map(({ pointId }) => pointId),
                    ...pointIdsExtraFromServer,
                ];

                const { result: pointsFromServer } =
                    await getPointInfoCollection({ ids: pointIds });

                setPoints(
                    pointsFromServer.map((m) => ({
                        ...m,
                        unplanned: pointIdsExtraFromServer.includes(m.id),
                    })),
                );
            } catch (error) {
                const { displayMessage } = getApiError(error);

                setError(displayMessage);

                notification.error({
                    title: "Не вдалося завантажити дані. Перевірте з'єднання та спробуйте знову.",
                });
            } finally {
                setLoading(false);
            }
        };

        getData();
    }, [notification, currentRoute?.agentInfo?.id, routeId, selectedDate]);

    const filteredPoints = points.filter(
        (m) => m.name?.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    return (
        <>
            <Meta title="Routes view by days of month" />
            <Header
                title={`Маршрут ${currentRoute?.name} за днями місяця`}
                back
            />

            <MainContainer>
                <Flex vertical gap={16}>
                    {/* Controls */}
                    <Flex vertical gap={12}>
                        <AppDatePicker
                            size="large"
                            disabled={offlineMode || loading}
                            defaultValue={selectedDate}
                            format={"DD MMMM YYYYр."}
                            allowClear={false}
                            onChange={(date) => {
                                if (date && !Array.isArray(date))
                                    setSelectedDate(date);
                            }}
                        />
                        <Input.Search
                            size="large"
                            disabled={loading || !!error}
                            placeholder="Пошук ТТ"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            allowClear
                        />
                    </Flex>

                    {error && (
                        <Result
                            status="error"
                            title="Помилка завантаження"
                            subTitle={error}
                            icon={null}
                        />
                    )}

                    {/* Cards */}
                    {!error && (
                        <Flex vertical gap={12}>
                            {loading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <PointCardSkeleton key={i} />
                                ))
                            ) : filteredPoints.length === 0 ? (
                                <Empty
                                    description={
                                        searchQuery
                                            ? "Нічого не знайдено"
                                            : "Немає торгових точок на цю дату"
                                    }
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            ) : (
                                filteredPoints.map((point) => (
                                    <PointCardItem
                                        key={point.id}
                                        point={point}
                                    />
                                ))
                            )}
                        </Flex>
                    )}
                </Flex>
            </MainContainer>
        </>
    );
};

export default ViewByDaysOfMonth;
